import { FormControl } from "@mui/material";
import clsx from "clsx";
import { isEqual } from "lodash";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import { PIC } from "@ryde/assets";
import { Address } from "@ryde/controls";
import { useGoogleServices } from "@ryde/redux";
import {
  AddStationFormFieldsEnum,
  IPassengersMapLocationInfo,
  IPlacePrediction,
  onActivateKeyDown,
  passengersMapAddStationSearchValueAtom,
  passengersMapCurrentPolylineViewAtom,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { usePassengerMapAddStationPanel } from "../../hooks";
import { PassengersAddStationPanelStyled } from "./passengers.map.styled";

interface IPassengersMapAddStationPanelProps {
  isOpen: boolean;
  handleClose: () => void;
}

export const PassengersMapAddStationPanel: FC<
  IPassengersMapAddStationPanelProps
> = ({ isOpen, handleClose }) => {
  const [currentOptions, setCurrentOptions] = useState<IPlacePrediction[]>([]);

  const [
    passengersMapAddStationSearchValue,
    setPassengersMapAddStationSearchValue,
  ] = useRecoilState(passengersMapAddStationSearchValueAtom);

  const setPassengersMapCurrentPolylineViewAtom = useSetRecoilState(
    passengersMapCurrentPolylineViewAtom,
  );

  const { getCoordinateByPlaceId } = useGoogleServices();
  const { methods, isDisabled, handleClick } = usePassengerMapAddStationPanel();
  const { control, setValue, watch } = methods;

  const AddressWatchField = watch(AddStationFormFieldsEnum.Addresses);

  const AddressedFieldsValue = useEvent(
    (val?: IPassengersMapLocationInfo): void => {
      setPassengersMapAddStationSearchValue(val);
      setValue(AddStationFormFieldsEnum.Addresses, val, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  );

  const handleOptionsChange = useCallback((options: IPlacePrediction[]) => {
    setCurrentOptions(options);
  }, []);

  const handleCurrentValueChange = async (
    newValue: IPlacePrediction | null,
  ): Promise<void> => {
    if (newValue) {
      const locationResult = await getCoordinateByPlaceId(newValue?.placeId);

      const { success, data } = locationResult ?? {};

      if (!success || !data?.lat || !data.lng) {
        AddressedFieldsValue(undefined);
        return warnAndToastSimpleResult(locationResult, t);
      }

      AddressedFieldsValue({
        fullAddress: newValue?.text?.text,
        latitude: locationResult.data?.lat as number,
        longitude: locationResult.data?.lng as number,
      });
    } else {
      AddressedFieldsValue(undefined);
    }
  };

  const { t } = useTranslation();

  const addressValue = useMemo(
    () =>
      AddressWatchField?.fullAddress
        ? ({
            placeId: "",
            text: { text: AddressWatchField.fullAddress },
          } as IPlacePrediction)
        : null,
    [AddressWatchField?.fullAddress],
  );

  useEffect(() => {
    if (!isEqual(AddressWatchField, passengersMapAddStationSearchValue)) {
      setValue(
        AddStationFormFieldsEnum.Addresses,
        passengersMapAddStationSearchValue,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }

    setPassengersMapCurrentPolylineViewAtom([]);
  }, [
    AddressWatchField,
    passengersMapAddStationSearchValue,
    setValue,
    AddressedFieldsValue,
    setPassengersMapCurrentPolylineViewAtom,
  ]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentOptions([]);
      setPassengersMapCurrentPolylineViewAtom([]);
      AddressedFieldsValue(undefined);
    }
  }, [isOpen, AddressedFieldsValue, setPassengersMapCurrentPolylineViewAtom]);

  return (
    isOpen && (
      <FormProvider {...methods}>
        <PassengersAddStationPanelStyled>
          <div className="dropdownsContainer">
            <div className="dropdownInput">
              <Controller
                name={AddStationFormFieldsEnum.Addresses}
                control={control}
                render={({ fieldState: { error } }) => {
                  return (
                    <FormControl
                      data-e2e={"passengers-address-map-template"}
                      error={!!error}
                      fullWidth
                    >
                      <Address
                        value={addressValue}
                        defaultOptions={currentOptions}
                        onChange={handleCurrentValueChange}
                        onOptionsChange={handleOptionsChange}
                        label={t("station.formLabel.locationInfo")}
                        className="addressAutoCompleteInput"
                      />
                    </FormControl>
                  );
                }}
              />
            </div>
          </div>

          <div className="actionsButtons">
            <div
              onClick={handleClick}
              onKeyDown={onActivateKeyDown(handleClick)}
              role="button"
              tabIndex={0}
              className={clsx("saveButton", { disabled: isDisabled })}
            >
              {t("general.SAVE")}
            </div>

            <PIC.XSVG className="close-icon-svg" onClick={handleClose} />
          </div>
        </PassengersAddStationPanelStyled>
      </FormProvider>
    )
  );
};
